import { gpHome } from '../../app';
import { Plan, ISubscription, IBillingInfo, IPaymentMethod, CurrentUserService } from '../../../app-common/services/current-user-service';
import { ILogglyService } from '../../../app-common/services/jsloggly-service';
import { CheckToReenableBillingDialogService } from '../gp-check-to-reenable-billing-dialog/gp-check-to-reenable-billing-dialog';
import { CancelSubscriptionDialogService } from '../gp-cancel-subscription-dialog/gp-cancel-subscription-dialog';
import { dayjs } from '#gp/app-common/util/dayjs';
import { formatEasyToReadDate } from '../../../app-common/util/dateFormatting';
import { PlanTier } from '../../../app-common/subscriptions/subscription-configuration-models';
import { ConfirmationDialogService, gpConfirmationDialogButtons } from '../../../app-common/components/confirmation-dialog';

import { describeSubscriptionStatus, countActiveSubscriptions, isSubscriptionWindingDown, SubscriptionStatusView } from './subscription-status';
import { AccountRefreshAllEvent, AccountPanelBusyEvent, AccountPanelBusyPayload } from '../../services/account-refresh-contract';
import { AiCreditsPurchaseServiceName, IAiCreditsPurchaseApi } from '../../services/ai-credits-purchase-contract';
import { isAiV2FeatureFlagEnabled } from '../../../app-common/util/feature-flags';

import type * as ng from 'angular';
import { LicenseInfo, LicenseInfoWithAssignedUser, LineItem, SingleSubscriptionInfo, StripeInvoiceSummary, SubscriptionInfo } from '../../../app-common/services/subscription.models';
import { gameplanProductConfiguration } from '../../../app-common/product-configuration';
import subscriptionControlTemplate from './subscription-control.html';
import { formatBillingAmount } from './subscription-billing-amount';

declare var htmx: any;

declare function gtag(action: string, info: string, args: {}): void;

interface SingleSubscriptionInfoViewModel {
    id: string;
    name: string;
    managementLink: string;
    lineItems: LineItem[];

    _showInvoices: boolean;
    _invoicesLoading: boolean;
    _invoicesLoaded: boolean;
    _invoicesError?: string | null | undefined;
    _invoices: StripeInvoiceSummary[];
    _sendInvoiceEmails: boolean | null;
    isEmailPreferencesLoaded: boolean;
    isEmailPreferencesSaving: boolean;
}

interface gpSubscriptionControlViewModel {
    subscriptionsManagedByMe: SingleSubscriptionInfoViewModel[];
}

/**
 * A subscription plus the presentation decisions the row needs. The flags are worked
 * out once, after the whole payload has been interpreted, rather than as a repeated
 * four-clause `ng-show` on every cell — which is how the old markup managed to show a
 * renewal date on a cancelling subscription.
 */
export interface SubscriptionRowViewModel extends ISubscription {
    statusView: SubscriptionStatusView;
    /** Renewal date: only for a live subscription that will actually bill again. */
    showRenewal: boolean;
    /**
     * The amount, which is not simply "whenever the renewal date shows". The server
     * nulls the billing price fields on a cancelling subscription and where it could
     * not read the upcoming invoice, and formatBillingAmount returns '' for a missing
     * amount or currency — so a renewal date can be known while the price is not, and
     * an AMOUNT heading over an empty space is worse than no heading at all. A zero
     * charge is not that case: a fully discounted renewal formats as $0.00 and shows.
     */
    showAmount: boolean;
    /** End date: the counterpart, for one that is winding down. */
    showEnds: boolean;
}

interface gpSubscriptionControlScope extends ng.IScope {
    info: SubscriptionInfo;
    isHandledByLicenseManager: boolean;
    purchaseOptions: PlanTier[];
    subscriptions: SubscriptionRowViewModel[];
    activeSubscriptionCount: number;
    licenses: LicenseInfo[];
    hasSomeAdminManagedOnlySubscriptions: boolean;
    showLicenceOptions(): void;
    refreshSubscriptionInfo(): void;
    startLicenseAssignment(license: LicenseInfoWithAssignedUser): void;
    cancelLicenseAssignment(license: LicenseInfoWithAssignedUser): void;
    assignLicense(license: LicenseInfoWithAssignedUser): void;

    /**
     * "Credit purchases" — the AI credit purchase invoices (#159), which sit here
     * rather than beside the credit balance because they are billing history, and this
     * is the panel that carries billing history.
     */
    showCreditPurchases: boolean;
    showCreditInvoices: boolean;
    creditInvoicesLoading: boolean;
    creditInvoicesLoaded: boolean;
    creditInvoicesError: string | null;
    creditInvoices: StripeInvoiceSummary[];
    toggleCreditInvoices(): void;

    toggleInvoices(subscription: SingleSubscriptionInfo): void;
    toggleInvoiceEmailPreference(subscription: SingleSubscriptionInfo): void;
    openSubscriptionManagementLink(whatToManage?: string): void;
    changeToSubscription(planCode: string): void;
    changePaymentMethod(): void;
    confirmStopPayment(): void;
    confirmCancelSubscription(): void;
    reactivateSubscription(): void;

    vm: gpSubscriptionControlViewModel

    loadingData: boolean;
    loadedData: boolean;

    /** Whether to offer the nominated licence options. True for every user. */
    showSubscriptionChoices: boolean;
    showCustomerPortalLink: boolean;
}

gpHome.directive('gpSubscriptionControl', [
    CurrentUserService.serviceName,
    'gpLogglyService',
    'gpCancelSubscriptionDialogService',
    'gpCheckToReenableBillingDialogService',
    ConfirmationDialogService.serviceName,
    AiCreditsPurchaseServiceName,
    '$timeout',
    function (
        gpCurrentUserService: CurrentUserService,
        gpLogglyService: ILogglyService,
        gpCancelSubscriptionDialog: CancelSubscriptionDialogService,
        gpCheckToReenableBillingDialog: CheckToReenableBillingDialogService,
        gpConfirmationDialogService: ConfirmationDialogService,
        gpAiCreditsPurchaseService: IAiCreditsPurchaseApi,
        $timeout: ng.ITimeoutService,
    ) {
        function link($scope: gpSubscriptionControlScope, element: ng.IAugmentedJQuery, attrs: ng.IAttributes) {
            $timeout(() => {
                //                htmx.process(element[0]);
            }, 100);

            $scope.vm = { subscriptionsManagedByMe: [] };

            // Credit purchases only mean anything where credits do.
            $scope.showCreditPurchases = isAiV2FeatureFlagEnabled();
            $scope.showCreditInvoices = false;
            $scope.creditInvoicesLoading = false;
            $scope.creditInvoicesLoaded = false;
            $scope.creditInvoicesError = null;
            $scope.creditInvoices = [];

            $scope.toggleCreditInvoices = () => {
                $scope.showCreditInvoices = !$scope.showCreditInvoices;
                if ($scope.showCreditInvoices && !$scope.creditInvoicesLoaded) {
                    $scope.creditInvoicesLoading = true;
                    $scope.creditInvoicesError = null;
                    gpAiCreditsPurchaseService.getInvoices()
                        .then(invoices => { $scope.creditInvoices = invoices; $scope.creditInvoicesLoaded = true; })
                        .catch(() => { $scope.creditInvoicesError = 'Unable to load invoices.'; })
                        .finally(() => { $scope.creditInvoicesLoading = false; });
                }
            };


            // The upgrade path, and it works for every user rather than only for
            // one the old verdict called a viewer. Same nominated options as the
            // choice screen renders.
            $scope.showLicenceOptions = () => {
                htmx.ajax('POST', '/user-setup/subscription-options', { target: '#subscription-selection-row', swap: 'outerHTML' })
            }

            function setAssignBusy(license: LicenseInfoWithAssignedUser, isBusy: boolean, status?: string) {
                (license as any)._assignBusy = isBusy;
                (license as any)._assignStatus = status || '';
            }

            function showErrorDialog(message: string) {
                return gpConfirmationDialogService.show({
                    title: 'Unable to assign licence',
                    mainInstruction: message,
                    buttons: [gpConfirmationDialogButtons.Ok],
                });
            }

            function confirmAssignment(licenseName: string, email: string) {
                return gpConfirmationDialogService.show({
                    title: 'Assign licence',
                    mainInstruction: `Assign ${licenseName} to ${email}?`,
                    buttons: [
                        { caption: 'Assign', result: 'assign' },
                        { caption: 'Cancel', result: 'cancel' },
                    ],
                });
            }

            function confirmInvite(email: string) {
                const productName = gameplanProductConfiguration().productName;
                return gpConfirmationDialogService.show({
                    title: 'Assign license to new user',
                    mainInstructionMarkdown: `No ${productName} account found for **${email}**.

Please check the email address. If it's correct, you can assign this license to the new user.

Instructions to send the new user:
1. Go to the ${productName} login page
2. Enter your email address and click 'Forgot password?'
3. Create a new password and log in`,
                    buttons: [
                        { caption: 'Assign', result: 'invite' },
                        { caption: 'Cancel', result: 'cancel' },
                    ],
                });
            }

            $scope.startLicenseAssignment = (license: LicenseInfoWithAssignedUser) => {
                const currentEmail = license.assignedTo && license.assignedTo.email ? license.assignedTo.email : '';
                (license as any)._assignEmail = currentEmail;
                (license as any)._assigning = true;
            };

            $scope.cancelLicenseAssignment = (license: LicenseInfoWithAssignedUser) => {
                (license as any)._assigning = false;
                (license as any)._assignEmail = '';
            };

            $scope.assignLicense = (license: LicenseInfoWithAssignedUser) => {
                const licenseName = license.name || license.type || 'licence';
                const email = ((license as any)._assignEmail || '').trim();

                if (!email) {
                    showErrorDialog('Please enter an email address to assign this licence.');
                    return;
                }

                confirmAssignment(licenseName, email)
                    .then(result => {
                        if (result !== 'assign') {
                            return;
                        }

                        setAssignBusy(license, true, 'Checking...');
                        return gpCurrentUserService.checkLicenseReassignment({
                            licenseId: license.id,
                            newlyAssignedEmail: email,
                        })
                            .then(checkResponse => {
                                setAssignBusy(license, false);
                                if (!checkResponse || checkResponse.canAssign !== true) {
                                    const reason = checkResponse?.cantAssignReason || 'We could not assign this licence.';
                                    showErrorDialog(reason);
                                    return;
                                }

                                const doAssign = (confirmInvite: boolean) => {
                                    setAssignBusy(license, true, 'Assigning...');
                                    return gpCurrentUserService.assignLicense({
                                        licenseId: license.id,
                                        newlyAssignedEmail: email,
                                        confirmInviteNewUser: confirmInvite,
                                    }).then(assignResponse => {
                                        setAssignBusy(license, false);
                                        if (assignResponse && assignResponse.success) {
                                            (license as any)._assigning = false;
                                            (license as any)._assignEmail = '';
                                            $scope.refreshSubscriptionInfo();
                                            return;
                                        }

                                        const error = assignResponse?.error || 'We could not assign this licence.';
                                        showErrorDialog(error);
                                    }).catch(() => {
                                        setAssignBusy(license, false);
                                        showErrorDialog('We could not assign this licence.');
                                    });
                                };

                                if (checkResponse.wouldInviteNewUser) {
                                    return confirmInvite(email)
                                        .then(inviteResult => {
                                            setAssignBusy(license, false);
                                            if (inviteResult !== 'invite') {
                                                return;
                                            }
                                            return doAssign(true);
                                        });
                                }

                                return doAssign(false);
                            })
                            .catch(() => {
                                setAssignBusy(license, false);
                                showErrorDialog('We could not validate this licence assignment.');
                            });
                    })
                    .catch(() => { });
            };

            /** Lets the page header's "Refresh all" spinner track this panel's fetches. */
            function reportBusy(busy: boolean) {
                $scope.$emit(AccountPanelBusyEvent, { panel: 'subscriptions', busy } satisfies AccountPanelBusyPayload);
            }

            function refreshSubscriptionInfoOnly() {
                $scope.loadingData = true;
                reportBusy(true);

                const subscriptionInfoRequest = gpCurrentUserService.getSubscriptionInfo()
                    .then(info => {
                        info.subscriptions = info.subscriptions || [];

                        $scope.purchaseOptions = info.purchaseOptions;
                        // Held locally until the payload has been fully interpreted: the viewer
                        // path below can replace the whole list, and the row flags can only be
                        // worked out once it is settled.
                        let subscriptionRows: ISubscription[] = (info.subscriptions || []).map(srcSubscription => formatSubscription(srcSubscription));
                        $scope.licenses = info.licenses || [];
                        $scope.isHandledByLicenseManager = info.isHandledByLicenseManager;

                        // Deduplicate licensesManagedByOthers by id, merging ownedBy into an array
                        try {
                            const raw = info.licensesManagedByOthers || [];
                            const grouped = new Map<string, any>();
                            for (const license of raw) {
                                const existing = grouped.get(license.id);
                                if (existing) {
                                    if (license.ownedBy) {
                                        existing.ownedByList.push(license.ownedBy);
                                    }
                                } else {
                                    grouped.set(license.id, {
                                        ...license,
                                        ownedByList: license.ownedBy ? [license.ownedBy] : [],
                                    });
                                }
                            }
                            info.licensesManagedByOthers = Array.from(grouped.values());
                        } catch (e) {
                            // If grouping fails, ensure each entry still has an ownedByList so the template renders
                            (info.licensesManagedByOthers || []).forEach((license: any) => {
                                if (!license.ownedByList) {
                                    license.ownedByList = license.ownedBy ? [license.ownedBy] : [];
                                }
                            });
                        }

                        $scope.info = info;
                        $scope.vm = {
                            subscriptionsManagedByMe: (info.subscriptionsManagedByMe || []).map(subscription => ({
                                ...subscription,
                                isEmailPreferencesLoaded: false,
                                isEmailPreferencesSaving: false,
                                _invoices: [],
                                _invoicesLoaded: false,
                                _invoicesLoading: false,
                                _sendInvoiceEmails: null,
                                _showInvoices: false,
                            } satisfies SingleSubscriptionInfoViewModel))
                        }

                        $scope.hasSomeAdminManagedOnlySubscriptions = subscriptionRows.length > 0
                            && !subscriptionRows.every(s => s.hasCustomerPortal);

                        // The rows, and the count beside the card title. Every row here is a
                        // real subscription now: the viewer path used to substitute a
                        // placeholder licence that rendered as a row and never billed, and
                        // licences are listed as licences instead.
                        $scope.subscriptions = subscriptionRows.map(subscription => {
                            const statusView = describeSubscriptionStatus(subscription);
                            const billable = !!subscription.nextBillingAt;
                            const windingDown = isSubscriptionWindingDown(subscription);

                            const showRenewal = billable && !windingDown;

                            return {
                                ...subscription,
                                statusView,
                                showRenewal,
                                showAmount: showRenewal && !!subscription.nextBillingPriceFormatted,
                                showEnds: billable && windingDown,
                            };
                        });
                        $scope.activeSubscriptionCount = countActiveSubscriptions($scope.subscriptions);

                        $scope.loadedData = true;

                        // Offered to EVERYONE, and this is the line that used to take them
                        // away. It cleared the flag for anyone the licence system served —
                        // which is everyone under one model — so leaving it in place would
                        // remove the upgrade path from the whole product. Holding a licence
                        // is not a reason to hide the options; the list already offers what
                        // the configuration nominates, and buying again is how a user with
                        // a lapsed licence gets another one.
                        $scope.showSubscriptionChoices = true;

                        $scope.showCustomerPortalLink = false;
                    })
                    .finally(() => {
                        $scope.loadingData = false;
                        reportBusy(false);
                    });

                const subscriptionStatusRequest = gpCurrentUserService.getSubscriptionStatus()
                    .then(status => {
                    });

                return subscriptionInfoRequest;
            }


            function checkAndrefreshSubscriptionInfo() {
                gpCurrentUserService.checkSubscriptionPayment().then(() => refreshSubscriptionInfoOnly());
            }


            $scope.refreshSubscriptionInfo = checkAndrefreshSubscriptionInfo;
            checkAndrefreshSubscriptionInfo();

            // "Refresh all" in the page header. Reloads in place — the panel keeps the
            // figures it already has until the new ones land (#544).
            $scope.$on(AccountRefreshAllEvent, () => {
                if (!$scope.loadingData) checkAndrefreshSubscriptionInfo();
            });

            $scope.toggleInvoices = (subscription: SingleSubscriptionInfoViewModel) => {
                subscription._showInvoices = !subscription._showInvoices;

                if (subscription._showInvoices && !subscription._invoicesLoaded) {
                    subscription._invoicesLoading = true;
                    subscription._invoicesError = null;

                    const invoicesReq = gpCurrentUserService.getSubscriptionInvoices(subscription.id);
                    const emailPrefReq = gpCurrentUserService.getInvoiceEmailPreference(subscription.id);

                    invoicesReq
                        .then(invoices => {
                            subscription._invoices = invoices;
                            subscription._invoicesLoaded = true;
                        })
                        .catch(() => {
                            subscription._invoicesError = 'Unable to load invoices.';
                        })
                        .finally(() => {
                            subscription._invoicesLoading = false;
                        });

                    emailPrefReq
                        .then(pref => {
                            subscription._sendInvoiceEmails = pref.sendInvoiceEmails;
                            subscription.isEmailPreferencesLoaded = true;
                        })
                        .catch(() => {
                            subscription._sendInvoiceEmails = false;
                            subscription.isEmailPreferencesLoaded = true;
                        });
                }
            };

            $scope.toggleInvoiceEmailPreference = (subscription: SingleSubscriptionInfoViewModel) => {
                const oldValue = subscription._sendInvoiceEmails;
                const newValue = !subscription._sendInvoiceEmails;
                subscription.isEmailPreferencesSaving = true;

                $timeout(() => {
                    gpCurrentUserService.setInvoiceEmailPreference(subscription.id, newValue)
                        .then(result => {
                            subscription._sendInvoiceEmails = result.sendInvoiceEmails;
                            subscription.isEmailPreferencesLoaded = true;
                        })
                        .catch(() => {
                            // revert on failure - value stays as it was
                        })
                        .finally(() => {
                            subscription.isEmailPreferencesSaving = false;
                        });
                }, 3000)

            };

            $scope.openSubscriptionManagementLink = (whatToManage?: string) => {
                gpCurrentUserService.getCustomerPortalLink(window.location.origin + '/account', whatToManage)
                    .then(info => {
                        if (info.url) {
                            window.open(info.url, '_blank');
                        } else {
                            console.error('No portal URL returned from backend.');
                        }
                    })
                    .catch(() => { })
            };


        }

        return ({
            restrict: 'E',
            scope: {
            },
            link: (scope: gpSubscriptionControlScope,
                instanceElement: ng.IAugmentedJQuery,
                attrs: ng.IAttributes) => {

                link(scope, instanceElement, attrs)

            },
            template: subscriptionControlTemplate,
        });
    }
]);


function formatSubscription(subscription: ISubscription | null | undefined): ISubscription {
    if (subscription) {
        return {
            ...subscription,
            nextBillingAt: subscription.nextBillingAt ? formatEasyToReadDate(dayjs(subscription.nextBillingAt).local()) : "",
            currentTermEndsAt: subscription.currentTermEndsAt ? formatEasyToReadDate(dayjs(subscription.currentTermEndsAt).local()) : "",
            nextBillingPriceFormatted: formatBillingAmount(subscription.nextBillingPrice, subscription.nextBillingPriceCurrency),
        }
    } else {
        return {
            id: '',
            quantity: 0,
            plan: { planDescription: "unknown", planId: "none", planName: "unknown name", planPrice: "" },
            isCancelling: false,
            autoCollect: false,
            nextBillingAt: '',
            nextBillingPrice: 0,
            nextBillingPriceCurrency: '',
            nextBillingPriceFormatted: '',

            creditBalance: 0,
            creditBalanceFormatted: '',

            currentTermEndsAt: '',

            status: '',

            hasCustomerPortal: false,
        }
    }
}
